import React, { useEffect } from "react";
import { useState } from "react";
import "../../css/common.css";
import "../../css/chart.css";
import PieChart from "../../common/chart/PieChart";
import LineChart from "../../common/chart/LineChart";
import BarChart from "../../common/chart/BarChart";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import {
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Grid,
  CircularProgress,
} from "@mui/material";
import { ApiService } from "../../services/apiService";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const theme = createTheme();

export default function Dashboard() {
  const [alignment, setAlignment] = useState("fifteen");
  const [totalCount, setTotalCount] = useState(0);
  const [androidCount, setAndroidCount] = useState(0);
  const [iosCount, setIosCount] = useState(0);
  const [androidData, setAndroidData] = useState({});
  const [iosData, setIosData] = useState({});
  const [mobileAppData, setMobileAppData] = useState({});
  const [devicesData, setDevicesData] = useState({});
  const [dailyUserData, setDailyUserData] = useState({});
  const [loading, setLoading] = useState(false);
  let params = "";
  let lineChartData = [];
  const dateData = [];

  const handleChange = (event, newAlignment) => {
    setAlignment(newAlignment);
  };

  useEffect(() => {
    getDateStartToLast();
    getTotalCount();
    getDashboardCount();
  }, []);

  const getTotalCount = async () => {
    setLoading(true);
    try {
      params = "";
      const totalCount = await ApiService.getUserList(params);
      setTotalCount(totalCount.data.total);
    } catch (error) {
      console.log("Dashboard - getTotalCount - error :: ", error);
    } finally {
      setLoading(false);
    }

    try {
      params = `contains={"ostype":"0"}`;
      const androidCount = await ApiService.getUserList(params);
      setAndroidCount(androidCount.data.total);
    } catch (error) {
      console.log("Dashboard - getAndroidCount - error :: ", error);
    } finally {
      setLoading(false);
    }

    try {
      params = `contains={"ostype":"1"}`;
      const isoCount = await ApiService.getUserList(params);
      setIosCount(isoCount.data.total);
    } catch (error) {
      console.log("Dashboard - getIsoCount - error :: ", error);
    } finally {
      setLoading(false);
    }
  };

  const getDashboardCount = async () => {
    try {
      setLoading(true);
      const dashboardCount = await ApiService.getDashboardCount();
      setAndroidData(dashboardCount.data["android"]);
      setIosData(dashboardCount.data["ios"]);
      setMobileAppData(dashboardCount.data["mobile_app"]);
      setDevicesData(dashboardCount.data["devices"]);
      dateUserList(dashboardCount.data["daily_users"]);
    } catch (error) {
      console.log("Dashboard - getDashboardCount - error :: ", error);
    } finally {
      setLoading(false);
    }
  };

  const clickSeven = () => {
    setAlignment("seven");
  };

  const clickFifteen = () => {
    setAlignment("fifteen");
  };

  const clickThirty = () => {
    setAlignment("thirty");
  };

  const getDateStartToLast = () => {
    const today = new Date();
    const diffDate = new Date(today);
    diffDate.setDate(today.getDate() - 29);

    while (diffDate <= today) {
      dateData.push(diffDate.toISOString().split("T")[0]);
      diffDate.setDate(diffDate.getDate() + 1);
    }
  };

  const dateUserList = (result) => {
    for (const [key, value] of Object.entries(result)) {
      lineChartData.push({ x: key, y: value });
    }

    //신규 사용자 수가 있는 날짜들만 api로 내려주기 때문에
    //신규 가입자가 없는 날짜는 따로 0명으로 넣어주어야함
    const otherData = dateData.filter(
      (data) => !Object.keys(result).includes(data)
    );

    for (const data of otherData) {
      lineChartData.push({ x: data, y: 0 });
    }

    setDailyUserData(
      lineChartData.sort((a, b) => new Date(a.x) - new Date(b.x))
    );
  };

  return (
    <div className="dashboardList">
      <ThemeProvider theme={theme}>
        <Grid
          container
          sx={{
            p: "10px",
            height: "100vh",
            display: "flex",
            flexWrap: "wrap",
            boxSizing: "border-box",
          }}
          xs={12}
        >
          <Grid
            item
            xs={12}
            sx={{ width: "100%", pt: "3px", pb: "5px", pl: "5px" }}
          >
            <Paper
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                border: "solid 1px #9db4c0",
                p: "5px",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  color: "black",
                  fontWeight: "bold",
                  marginBottom: "3px",
                }}
              >
                사용자수
              </div>
              <div
                style={{ fontSize: "18px", color: "black", fontWeight: "bold" }}
              >
                <span style={{ padding: "0 25px" }}>
                  <PersonRoundedIcon
                    sx={{ fontSize: "18px", pr: 1, verticalAlign: "-3px" }}
                  />
                  Total : {totalCount} 명
                </span>
                <span style={{ padding: "0 25px" }}>
                  <PersonRoundedIcon
                    sx={{ fontSize: "18px", pr: 1, verticalAlign: "-3px" }}
                  />
                  Android : {androidCount} 명
                </span>
                <span style={{ padding: "0 25px" }}>
                  <PersonRoundedIcon
                    sx={{ fontSize: "18px", pr: 1, verticalAlign: "-3px" }}
                  />
                  iOS : {iosCount} 명
                </span>
              </div>
            </Paper>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={4} sx={{ pt: "5px", pb: "5px", pl: "5px" }}>
              <Paper
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  border: "solid 1px #9db4c0",
                  p: "5px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {loading ? (
                  <CircularProgress sx={{ pt: "120px", pb: "120px" }} />
                ) : (
                  <PieChart
                    labels={Object.keys(iosData)}
                    series={Object.values(iosData)}
                    title="OS 버전별 사용자수(IOS)"
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={4} sx={{ pt: "5px", pb: "5px", pl: "10px" }}>
              <Paper
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  border: "solid 1px #9db4c0",
                  p: "5px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {loading ? (
                  <CircularProgress sx={{ pt: "120px", pb: "120px" }} />
                ) : (
                  <PieChart
                    labels={Object.keys(androidData)}
                    series={Object.values(androidData)}
                    title="OS 버전별 사용자수(Android)"
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={4} sx={{ pt: "5px", pb: "5px", pl: "10px" }}>
              <Paper
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  border: "solid 1px #9db4c0",
                  p: "5px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {loading ? (
                  <CircularProgress sx={{ pt: "120px", pb: "120px" }} />
                ) : (
                  <PieChart
                    labels={Object.keys(mobileAppData)}
                    series={Object.values(mobileAppData)}
                    title="모바일 앱 버전별 사용자수"
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ width: "100%", pt: "5px", pb: "5px", pl: "5px" }}
          >
            <Paper
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                border: "solid 1px #9db4c0",
                p: "5px",
              }}
            >
              <div className="chartItem">
                <div className="chartTitle">일별 신규 사용자수</div>
                <div className="toggleGroup">
                  <ToggleButtonGroup
                    onChange={handleChange}
                    sx={{ height: "30px", mr: "20px" }}
                    color="primary"
                    value={alignment}
                    exclusive
                    aria-label="Platform"
                  >
                    <ToggleButton value="seven" onClick={clickSeven}>
                      7일
                    </ToggleButton>
                    <ToggleButton value="fifteen" onClick={clickFifteen}>
                      15일
                    </ToggleButton>
                    <ToggleButton value="thirty" onClick={clickThirty}>
                      30일
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>
              {loading ? (
                <CircularProgress sx={{ pt: "120px", pb: "120px" }} />
              ) : (
                <LineChart chartResult={dailyUserData} alignment={alignment} />
              )}
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ width: "100%", pt: "5px", pb: "15px", pl: "5px" }}
          >
            <Paper
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                border: "solid 1px #9db4c0",
                p: "5px",
              }}
            >
              {loading ? (
                <CircularProgress sx={{ pt: "120px", pb: "120px" }} />
              ) : (
                <BarChart
                  categories={Object.keys(devicesData)}
                  data={Object.values(devicesData)}
                  title={"디바이스별 사용자수"}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </ThemeProvider>
    </div>
  );
}
